import { useEffect, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { resizeImage } from "../lib/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";

const EMPTY = { name: "", branch: "", address: "", area: "", district: "", state: "West Bengal", pincode: "", phone: "", photo: "", lat: "", lng: "", maps_url: "", active: true, sort_order: 0 };

const parseCoords = (url) => {
  const m = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/) || url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/) || url.match(/[?&](?:q|query|ll|destination)=(-?\d+\.\d+),\s*(-?\d+\.\d+)/);
  return m ? [m[1], m[2]] : null;
};

export default function ShopDialog({ shop, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (shop) setF({ ...EMPTY, ...shop, lat: shop.lat ?? "", lng: shop.lng ?? "" });
  }, [shop]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onMapsUrl = (v) => {
    const c = parseCoords(v);
    setF((s) => ({ ...s, maps_url: v, ...(c ? { lat: c[0], lng: c[1] } : {}) }));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) set("photo", await resizeImage(file));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { id, ...rest } = f;
    const num = (v) => (v === "" || v === null ? null : Number(v));
    const body = { ...rest, lat: num(f.lat), lng: num(f.lng), sort_order: Number(f.sort_order) || 0 };
    try {
      if (shop?.id) await api.put(`/shops/${shop.id}`, body);
      else await api.post("/shops", body);
      toast.success("Shop saved");
      onSaved();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const input = (k, label, props = {}) => (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-navy">{label}</span>
      <input data-testid={`shop-form-${k}`} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="field" {...props} />
    </label>
  );

  return (
    <Dialog open={!!shop} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-shop-dialog" className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>{shop?.id ? "Edit shop" : "Add shop"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2 flex items-center gap-4">
            {f.photo ? <img src={f.photo} alt="" className="h-24 w-32 rounded-xl object-cover" /> : <div className="grid h-24 w-32 place-items-center rounded-xl bg-slate-100 text-xs text-slate-500">No photo</div>}
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-sm font-semibold text-navy hover:border-brand">
              <Upload className="h-4 w-4" /> {f.photo ? "Replace photo" : "Upload shop photo"}
              <input data-testid="shop-form-photo-upload" type="file" accept="image/*" onChange={onFile} className="hidden" />
            </label>
          </div>
          {input("name", "Shop name *", { required: true, placeholder: "e.g. MHM SUPER ADBLUE – Haldia" })}
          {input("branch", "Branch number / label", { placeholder: "e.g. Branch 1" })}
          <div className="sm:col-span-2">{input("address", "Full address")}</div>
          {input("area", "Area / City")}
          {input("district", "District")}
          {input("state", "State")}
          {input("pincode", "PIN code", { inputMode: "numeric" })}
          {input("phone", "Contact number")}
          <div className="sm:col-span-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-navy">Google Maps link</span>
              <input data-testid="shop-form-maps_url" value={f.maps_url} onChange={(e) => onMapsUrl(e.target.value)} className="field" placeholder="Paste the shop's Google Maps link" />
            </label>
            <p className="mt-1 text-xs text-slate-500">If the link contains coordinates they fill in automatically. For short share links, open the link on a computer, copy the full address bar URL, or right-click the pin in Google Maps to copy its coordinates.</p>
          </div>
          {input("lat", "Latitude (for map marker)", { type: "number", step: "any", placeholder: "e.g. 22.0667" })}
          {input("lng", "Longitude (for map marker)", { type: "number", step: "any", placeholder: "e.g. 88.0698" })}
          {input("sort_order", "Display order", { type: "number" })}
          <label className="flex items-end gap-2 pb-3 text-sm font-semibold text-navy"><Switch data-testid="shop-form-active" checked={f.active} onCheckedChange={(v) => set("active", v)} /> Show on website</label>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-navy hover:bg-slate-100">Cancel</button>
            <button data-testid="shop-form-save" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save shop
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
